import {
  Dispatch, RefObject, SetStateAction, useMemo,
} from 'react';
import { SetMirrorData } from '@type/job-add';
import { RecordInterface } from '@type/form-types';
import { Button, ListboxWrapper } from '@components/common/Forms';
import {
  ListboxStyled, ListboxStyledButton, ListboxStyledOptions, ListboxStyledOption,
} from '@components/common';
import { RadioCheckedIcon } from '@components/common/Icons';
import { ChevronUpDownIcon } from '@heroicons/react/16/solid';
import { ItemOption } from '@components/templates/job-add/edit/form/partials/ItemOption';
import { dataTestId } from '@services/data-test-id';

/** Types */
type BundleType = ApiSchema<'ActivationBundle'>;

interface ActivationTypeProps {
  jobActivationItems?: BundleType[];
  initialIndex?: number;
  setSelectedActivation?: Dispatch<SetStateAction<BundleType | undefined>>;
  record?: RecordInterface;
  disabled?: boolean;
  setMirrorData?: SetMirrorData;
  hasInteracted?: RefObject<boolean>;
  lastSelectedIdentifier?: RefObject<string | undefined>;
}

/**
 * @description Activation type component.
 * - `jobActivationItems`: The job activation items (bundles + converted subscriptionPackage).
 * - `initialIndex`: The initial selected index.
 * - `setSelectedActivation`: The selected activation setter.
 * - `record`: The Bj form record function.
 * - `disabled`: If the component is disabled. Usually when only extending the job (context=extend).
 * - `hasActiveSubscription`: If the user has an active subscription.
 * - `setMirrorData`: The mirror data setter.
 * - `hasInteracted`: The has interacted ref.
 * - `lastSelectedIdentifier`: The last selected identifier ref.
 */
export const ActivationType = (props: ActivationTypeProps) => {
  // Destructure props
  const {
    jobActivationItems = [],
    initialIndex = 1,
    setSelectedActivation,
    record,
    disabled,
    setMirrorData,
    hasInteracted,
    lastSelectedIdentifier,
  } = props;

  // Classes
  const listboxOption = useMemo(() => ({
    button: 'flex min-h-20 w-full items-center justify-between gap-4 rounded border px-4 py-3 text-left ring-3 sm:px-5',
    buttonColors: 'border-secondary/80 ring-secondary/30',
    buttonDisabled: 'cursor-not-allowed border-input ring-ink/10',
    base: 'flex gap-2 px-3 ui-active:bg-surface-50 ui-not-active:bg-surface ui-not-active:text-ink',
    check: 'size-6 ui-selected:block ui-selected:text-secondary shrink-0 mt-3 sm:mt-3.5',
    uncheck: 'size-5 rounded-full border-2 border-ink/30 border-dotted shrink-0 mx-0.5 mt-3 sm:mt-4',
  }), []);

  // Return null if there are no activation items
  if (!jobActivationItems || jobActivationItems.length === 0) return null;

  // Render the listbox
  // *************************
  return (
    <ListboxWrapper
      recordFunc={record}
      fieldName="packageId"
      data={jobActivationItems}
      defaultValueKey="packageId"
      labelKey="title"
      initialSelectedIndex={[initialIndex]}
      withTranslation
      renderListbox={({
        lsData,
        selected,
        setSelected,
      }) => {
        const selectedItem = selected as BundleType;
        return (
          <ListboxStyled
            disabled={disabled}
            className="relative"
            by="label"
            value={selected}
            onChange={(value) => {
              const selectedValue = value as BundleType;
              // 1. Update refs FIRST and ATOMICALLY
              if (hasInteracted) {
                hasInteracted.current = true;
              }
              if (lastSelectedIdentifier) {
                lastSelectedIdentifier.current = selectedValue.identifier;
              }

              // 2. Update local state
              setSelected(selectedValue);
              setSelectedActivation?.(selectedValue);

              // 3. Trigger side effects LAST
              setMirrorData?.({
                packageId: selectedValue.packageId,
                // keep the currently selected bundle identifier in mirror data so dependent components can react
                bundle: selectedValue.identifier,
              });
            }}
          >
            {/* Button */}
            <ListboxStyledButton
              as={Button}
              styling="none"
              className={`${listboxOption.button} ${disabled ? listboxOption.buttonDisabled : listboxOption.buttonColors}`}
              {...dataTestId('jobAddPage.jobActivation.openDropdownButton')}
            >
              <ItemOption
                itemData={selectedItem}
              />
              <ChevronUpDownIcon className="-mr-2 size-5 shrink-0 text-ink/80" />
            </ListboxStyledButton>

            {/* Options */}
            <ListboxStyledOptions
              className="mt-0.5 w-full"
              animation="slideDown"
              position="left"
              {...dataTestId('jobAddPage.jobActions.bundleList')}
            >

              {/* Activation items */}
              {lsData.map((field, index) => (
                <ListboxStyledOption
                  key={field.packageId}
                  value={field}
                  disabled={!field?.available}
                  className={`${listboxOption.base} ${!field.available ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                  data-test-id={`jobAdd-activation-option_${field?.identifier || index}`}
                >
                  {/* Checkmark */}
                  {selectedItem?.packageId === field?.packageId
                    ? (<RadioCheckedIcon className={listboxOption.check} />)
                    : (<span className={listboxOption.uncheck} />)}

                  {/* Content */}
                  <ItemOption
                    itemData={field}
                    className="border-t border-dashed py-3 sm:py-3.5"
                  />
                </ListboxStyledOption>
              ))}

            </ListboxStyledOptions>
          </ListboxStyled>
        );
      }}
    />
  );
};
